import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveDailyReportPhotoMutation,
  SaveDailyReportPhoto_Input
} from 'generated/graphql';

export type IFormState = {
  id: string;
  apiFileQueue: string[];
  dailyReportId: string;
  uploadCount: number;
  photoCount: number;
  isUploading: boolean;
};

const initialState: IFormState = {
  id: '',
  apiFileQueue: [],
  dailyReportId: '',
  uploadCount: 0,
  photoCount: 0,
  isUploading: false
};

export function useFormState(dailyReportId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      apiFileQueue: {
        label: 'Api file id',
        type: 'text'
      },
      dailyReportId: {
        label: 'Daily report id',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (dailyReportId !== '' && formState.dailyReportId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'dailyReportId',
          value: dailyReportId
        }
      });
    }
  }, [
    dailyReportId,
    dispatch,
    formState.dailyReportId
  ]);

  useEffect(() => {
    if (formState.uploadCount > 0 && formState.uploadCount === formState.photoCount && formState.isUploading) {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'isUploading',
          value: false
        }
      });
    }
    if (formState.uploadCount > 0 && formState.uploadCount > formState.photoCount && !formState.isUploading) {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'isUploading',
          value: true
        }
      });
    }
    return () => {};
  }, [
    dispatch,
    formState.isUploading,
    formState.photoCount,
    formState.uploadCount
  ]);

  const [
    fnSavePhoto,
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    { loading, called }
  ] = useSaveDailyReportPhoto();

  useEffect(() => {
    const tempState = [...formState.apiFileQueue];
    if (tempState.length > 0) {
      const fileId = tempState.shift() ?? '';
      (async () => {
        await fnSavePhoto({
          apiFileId: fileId,
          dailyReportId: formState.dailyReportId
        });
      })();
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          apiFileQueue: tempState,
          photoCount: formState.photoCount + 1
        }
      });
    }
  }, [
    dispatch,
    fnSavePhoto,
    formState
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveDailyReportPhoto() {
  const [
    fnMutate,
    tuple
  ] = useSaveDailyReportPhotoMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveDailyReportPhoto_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
